// SCR-STAFF-1203 — بسته‌های تخصصی (US-CFG-032، US-PLT-051؛ FLOW-16) و بازبینی محتوای نمونه (US-CFG-035): پیش‌نمایش شمار اقلام هر بخش با جزئیات،
// انتخاب بخش‌های اختیاری، هشدار سیاست دید «فقط تیم درمان» و دسته حساس، سقف بسته‌های پلن (پیشنهاد افزونه — AC-PLT-051.2)، اعمال در پس‌زمینه با
// پیشرفت؛ اقلام نمونه با نوار «محتوای نمونه» و «تأیید برای استفاده» یا «جایگزین شد» برای نقش مجاز (Physician بالینی، TenantOwner حقوقی).
// همین صفحه مرحله ۳ Setup Wizard است.
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardActionArea from '@mui/material/CardActionArea';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  isApiError,
  type PackInstallView,
  type PackPreviewSection,
  type SampleItemView,
  type TenantPackView,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  ListPage,
  LoadingState,
  SampleBadge,
  SampleContentBanner,
  StatusChip,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { cfgKeys } from './common';

export const SCR_STAFF_1203 = 'SCR-STAFF-1203';

const INSTALL_TONE: Record<string, StatusTone> = { Running: 'pending', Completed: 'active', Failed: 'error' };
const REVIEW_TONE: Record<string, StatusTone> = { Pending: 'pending', Approved: 'active', Replaced: 'confirmed' };

function count(value: number | string | null | undefined): number {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function InstallProgress({ install }: { install: PackInstallView }) {
  const { t } = useTranslation('cfg');
  const total = count(install.totalItems);
  const processed = count(install.processedItems);
  return (
    <Stack spacing={0.5} data-testid="pack-install" data-status={install.status}>
      <Stack direction="row" spacing={1} alignItems="center">
        <StatusChip tone={INSTALL_TONE[install.status] ?? 'inactive'} labelKey={`cfg:packs.install.${install.status}`} />
        <Typography variant="body2">
          {t('cfg:packs.install.summary', {
            created: faDigits(count(install.createdItems)),
            linked: faDigits(count(install.linkedItems)),
            deferred: faDigits(count(install.deferredItems)),
          })}
        </Typography>
      </Stack>
      {install.status === 'Running' && (
        <LinearProgress variant="determinate" value={total === 0 ? 0 : Math.round((processed * 100) / total)} aria-label={t('cfg:packs.install.progress')} />
      )}
      {install.status === 'Failed' && <Alert severity="error">{t('cfg:packs.install.failed', { code: install.errorCode ?? '' })}</Alert>}
    </Stack>
  );
}

function SectionRow({ section, selected, onToggle }: { section: PackPreviewSection; selected: boolean; onToggle: (value: boolean) => void }) {
  const { t } = useTranslation('cfg');
  const info = section.count;
  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.5} data-testid={`pack-preview-section-${info.section}`}>
      {/* چک‌باکس بیرون از سرآیند آکاردئون (کنترل تعاملی تودرتو نباشد — axe nested-interactive). */}
      <Checkbox
        checked={selected}
        disabled={!info.optional}
        onChange={(event) => onToggle(event.target.checked)}
        sx={{ mt: 0.5 }}
        slotProps={{ input: { 'aria-label': t(`cfg:packs.sections.${info.section}`, { defaultValue: info.section }) } }}
      />
      <Accordion disableGutters variant="outlined" sx={{ flex: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <Typography sx={{ flex: 1 }}>
              {t(`cfg:packs.sections.${info.section}`, { defaultValue: info.section })} · {faDigits(count(info.items))}
              {count(info.samples) > 0 && ` (${t('cfg:packs.samples', { count: faDigits(count(info.samples)) })})`}
            </Typography>
            {!info.optional && <StatusChip tone="booked" labelKey="cfg:packs.required" />}
            {!info.supported && <StatusChip tone="inactive" labelKey="cfg:packs.coming_soon" />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {section.items.map((item) => (
              <Box component="li" key={item.localId} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                <span style={{ flex: 1 }}>{item.title}</span>
                {item.isSample && <SampleBadge />}
                <Typography variant="caption" color="text.secondary">
                  {t(`cfg:packs.item_state.${item.state}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function PackPreview({ pack, canApply }: { pack: TenantPackView; canApply: boolean }) {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [excluded, setExcluded] = useState<string[]>([]);
  const preview = useQuery({
    queryKey: cfgKeys.packPreview(session?.tenantId, pack.code),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/specialty-packs/{code}/preview', { params: { path: { code: pack.code } } })),
  });
  const install = useQuery({
    queryKey: cfgKeys.packInstall(session?.tenantId, pack.code),
    queryFn: async () => {
      try {
        return await unwrap(tenantApi.GET('/api/v1/configuration/specialty-packs/{code}/install', { params: { path: { code: pack.code } } }));
      } catch (error) {
        // هنوز اعمال نشده.
        if (isApiError(error) && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    refetchInterval: (query) => (query.state.data?.status === 'Running' ? 1000 : false),
  });
  const apply = useMutation({
    mutationFn: (sections: string[]) =>
      unwrap(tenantApi.POST('/api/v1/configuration/specialty-packs/{code}:apply', { params: { path: { code: pack.code } }, body: { sections } })),
    onSuccess: (view) => queryClient.setQueryData(cfgKeys.packInstall(session?.tenantId, pack.code), view),
  });
  const finished = install.data?.status === 'Completed';
  useEffect(() => {
    if (finished) {
      void queryClient.invalidateQueries({ queryKey: cfgKeys.packPreview(session?.tenantId, pack.code) });
      void queryClient.invalidateQueries({ queryKey: cfgKeys.packs(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: cfgKeys.samples(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: ['platform', 'onboarding'] });
    }
  }, [finished, pack.code, queryClient, session?.tenantId]);

  if (preview.isPending) {
    return <LoadingState rows={4} />;
  }
  if (preview.error || !preview.data) {
    return <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />;
  }
  const view = preview.data;
  const sections = view.sections.map((section) => section.count.section).filter((section) => !excluded.includes(section));
  const limitError = isApiError(apply.error) && apply.error.code === 'PHX-PLT-0402';
  const running = install.data?.status === 'Running' || apply.isPending;

  return (
    <Stack spacing={1.5} data-testid={`pack-preview-${pack.code}`}>
      {view.description && <Typography color="text.secondary">{view.description}</Typography>}
      {view.clinicalVisibility === 'CareTeamOnly' && (
        <Alert severity="warning" data-testid="pack-visibility-warning">
          {t('cfg:packs.care_team_warning')}
        </Alert>
      )}
      {count(view.sensitiveCategories) > 0 && <Alert severity="info">{t('cfg:packs.sensitive_warning')}</Alert>}
      {(view.limitReached || limitError) && (
        <Alert
          severity="warning"
          data-testid="pack-limit"
          action={
            <Button component={RouterLink} to={SUBSCRIPTION_PATH} size="small">
              {t('cfg:packs.limit_action')}
            </Button>
          }
        >
          <AlertTitle>{t('cfg:packs.limit_title')}</AlertTitle>
          {t('cfg:packs.limit_body', { max: faDigits(count(view.limitMax)) })}
        </Alert>
      )}
      {view.sections.map((section) => (
        <SectionRow
          key={section.count.section}
          section={section}
          selected={!excluded.includes(section.count.section)}
          onToggle={(value) =>
            setExcluded((current) => (value ? current.filter((key) => key !== section.count.section) : [...current, section.count.section]))
          }
        />
      ))}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          onClick={() => apply.mutate(sections)}
          disabled={!canApply || running || view.limitReached}
          data-testid="pack-apply"
        >
          {install.data ? t('cfg:packs.reapply') : t('cfg:packs.apply')}
        </Button>
        {apply.error && !limitError ? <Alert severity="error">{t('cfg:packs.apply_failed')}</Alert> : null}
      </Stack>
      {install.data && <InstallProgress install={install.data} />}
    </Stack>
  );
}

function PacksTab() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [selected, setSelected] = useState<string | null>(null);
  const packs = useQuery({
    queryKey: cfgKeys.packs(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/specialty-packs')),
  });
  const canApply = access.can('plt.onboarding.manage') && access.accessMode === 'Full';

  if (packs.isPending) {
    return <LoadingState rows={3} />;
  }
  if (packs.error || !packs.data) {
    return <ErrorState error={packs.error} onRetry={() => void packs.refetch()} />;
  }
  const current = packs.data.items.find((pack) => pack.code === selected) ?? null;
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary" data-testid="pack-limit-usage">
        {t('cfg:packs.limit_usage', {
          used: faDigits(count(packs.data.limitUsed)),
          max: packs.data.limitMax == null ? t('cfg:packs.unlimited') : faDigits(count(packs.data.limitMax)),
        })}
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {packs.data.items.map((pack) => (
          <Paper key={pack.code} variant="outlined" sx={{ flex: 1, borderColor: selected === pack.code ? 'primary.main' : undefined }}>
            {/* دکمه کامل کارت برای صفحه‌کلید و صفحه‌خوان (DoD: Keyboard navigation). */}
            <CardActionArea sx={{ p: 2 }} onClick={() => setSelected(pack.code)} aria-pressed={selected === pack.code} data-testid={`pack-card-${pack.code}`}>
              <Typography variant="h4" component="span" sx={{ display: 'block' }}>
                {pack.name}
              </Typography>
              <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
                {t('cfg:packs.version', { version: faDigits(count(pack.version)) })}
              </Typography>
              {pack.install && (
                <Box component="span" sx={{ display: 'block', mt: 1 }}>
                  <StatusChip tone={INSTALL_TONE[pack.install.status] ?? 'inactive'} labelKey={`cfg:packs.install.${pack.install.status}`} />
                </Box>
              )}
            </CardActionArea>
          </Paper>
        ))}
      </Stack>
      {current && <PackPreview key={current.code} pack={current} canApply={canApply} />}
    </Stack>
  );
}

function SamplesTab() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const samples = useQuery({
    queryKey: cfgKeys.samples(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/sample-items')),
  });
  const review = useMutation({
    mutationFn: ({ id, replaced }: { id: string; replaced: boolean }) =>
      unwrap(tenantApi.POST('/api/v1/configuration/sample-items/{id}:review', { params: { path: { id } }, body: { replaced } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cfgKeys.samples(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: ['platform', 'onboarding'] });
    },
  });

  const columns: DataTableColumn<SampleItemView>[] = [
    {
      id: 'title',
      headerKey: 'cfg:samples.item',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <SampleBadge />
          <span>{row.title ?? row.localId}</span>
        </Stack>
      ),
    },
    { id: 'section', headerKey: 'cfg:samples.section', cell: (row) => t(`cfg:packs.sections.${row.section}`, { defaultValue: row.section }) },
    { id: 'kind', headerKey: 'cfg:samples.kind', cell: (row) => t(`cfg:samples.kinds.${row.contentKind}`) },
    { id: 'status', headerKey: 'cfg:samples.status', cell: (row) => <StatusChip tone={REVIEW_TONE[row.reviewStatus] ?? 'inactive'} labelKey={`cfg:samples.statuses.${row.reviewStatus}`} /> },
    {
      id: 'actions',
      headerKey: 'common:actions.title',
      cell: (row) =>
        row.reviewStatus === 'Pending' ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={!row.canReview || review.isPending} onClick={() => review.mutate({ id: row.id, replaced: false })} data-testid={`sample-approve-${row.localId}`}>
              {t('cfg:samples.approve')}
            </Button>
            <Button size="small" disabled={!row.canReview || review.isPending} onClick={() => review.mutate({ id: row.id, replaced: true })} data-testid={`sample-replaced-${row.localId}`}>
              {t('cfg:samples.replaced')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <Stack spacing={1.5}>
      <SampleContentBanner>{t('cfg:samples.hint', { count: faDigits(count(samples.data?.pending)) })}</SampleContentBanner>
      {review.error ? <ErrorState error={review.error} /> : null}
      <DataTable
        columns={columns}
        rows={samples.data?.items}
        getRowId={(row) => row.id}
        isLoading={samples.isPending}
        error={samples.error}
        onRetry={() => void samples.refetch()}
        captionKey="cfg:samples.title"
        emptyTitleKey="cfg:samples.empty"
      />
    </Stack>
  );
}

export function SpecialtyPacksPage() {
  const { t } = useTranslation('cfg');
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const canPacks = access.can('plt.onboarding.manage');
  const canSamples = access.can('cfg.sample_content.review');
  const tab = params.get('tab') === 'samples' || !canPacks ? 'samples' : 'packs';
  return (
    <ListPage screenId={SCR_STAFF_1203} titleKey="cfg:packs.title" subtitleKey="cfg:packs.subtitle">
      <Tabs value={tab} onChange={(_event, value: string) => setParams(value === 'samples' ? { tab: 'samples' } : {}, { replace: true })} sx={{ mb: 2 }}>
        {canPacks && <Tab value="packs" label={t('cfg:packs.tab')} data-testid="tab-packs" />}
        {canSamples && <Tab value="samples" label={t('cfg:samples.title')} data-testid="tab-samples" />}
      </Tabs>
      {tab === 'packs' && canPacks ? <PacksTab /> : canSamples ? <SamplesTab /> : null}
    </ListPage>
  );
}
